import AutocompleteMultiInputNext from '@/buyers/components/AutocompleteMultiInputNext'
import ExternalVendorCodeSelector from '@/buyers/components/ExternalVendorCodeSelector'
import useSession from '@/buyers/hooks/useSession'
import Action from '@/gf/components/Action'
import Button from '@/gf/components/ButtonOld'
import Field from '@/gf/components/Field'
import FieldError from '@/gf/components/next/forms/FieldError'
import TextField from '@/gf/components/TextField'
import TextInput from '@/gf/components/TextInput'
import useMsgs from '@/gf/hooks/useMsgs'
import { ApolloError } from '@apollo/client'
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { yupResolver } from '@/gf/lib/yupResolver'
import { Fragment, useMemo } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import * as yup from 'yup'
import {
  SearchBrandsDocument,
  SearchBrandsQuery,
  SearchBrandsQueryVariables,
  ShippingLocationsSearchDocument,
  ShippingLocationsSearchQuery,
  ShippingLocationsSearchQueryVariables,
  useSearchBrandsQuery,
  useShippingLocationsSearchQuery,
  useUpdateVendorMutation,
  VendorQuery,
} from '../../_gen/gql'
import useGqlClient from '../../hooks/useGqlClient'
import { useContext } from '../Vendor'

interface VendorForm {
  accountNumbers: string[]
  name: string
  brandIds: string[]
  offline: boolean
  delivery: boolean
  fleetioId: number | null
  externalVendorId: string | null
  shippingLocationIds: string[]
}

// Zachry's SAP integration is gated by an org feature rather than an org_apps
// row, so the caller passes it in from the session (GEA-7397).
export const createVendorLabel = (org: VendorQuery['org'], isZachry = false) => {
  const showHcssId = org?.orgApps.some((oa) => oa.appId === 'hcss')
  const showAssetWorks = org?.orgApps.some((oa) => oa.appId === 'assetworks')

  if (isZachry) return 'SAP Vendor Number'

  return showAssetWorks ? 'AssetWorks Vendor ID' : showHcssId ? 'E360 Vendor ID' : 'Code'
}

const VendorForm = ({
  onAfterSave,
  onCancel,
}: {
  onAfterSave: () => void
  onCancel: () => void
}) => {
  const [_, messages] = useMsgs()
  const client = useGqlClient()
  const [updateVendor] = useUpdateVendorMutation({ client })
  const { vendor, org } = useContext()
  const { orgId, features } = useSession()
  const showFleetioId = org?.orgApps.some((oa) => oa.appId === 'fleetio')
  const isZachry = features.zachryIntegrationEnabled
  // A vendor that already carries a SAP number may not have it blanked back
  // out; one the org has carried since before the rule stays editable without
  // one (GEA-7397).
  const externalVendorIdRequired = isZachry && !!vendor.externalVendorId

  const vendorSchema = useMemo(
    () =>
      yup.object({
        accountNumbers: yup.array().of(yup.string().required().label('Account Number')),
        name: yup.string().required().label('Vendor Name'),
        brandIds: yup.array(yup.string()),
        shippingLocationIds: yup.array(yup.string()),
        offline: yup.boolean(),
        delivery: yup.boolean(),
        externalVendorId: externalVendorIdRequired
          ? yup.string().nullable().required('SAP Vendor Number is required')
          : yup.string().nullable().optional(),
        ...(showFleetioId
          ? {
              fleetioId: yup
                .number()
                .nullable()
                .required('Fleetio Vendor ID is required')
                .transform((v) => (Number.isNaN(v) ? null : v)),
            }
          : {}),
      }),
    [showFleetioId, externalVendorIdRequired]
  )

  // No `shouldUnregister`: no input registers `offline`, and react-hook-form 7.89 then leaves it
  // out of the submitted values, so every save sent `offline: false` (GEA-11423).
  const form = useForm<VendorForm>({
    shouldFocusError: true,
    defaultValues: {
      accountNumbers: vendor.accountNumbers || [''],
      name: vendor.name || '',
      brandIds: vendor.brands.map((b) => b.id),
      offline: vendor.offline,
      fleetioId: vendor.fleetioId,
      externalVendorId: vendor.externalVendorId,
      shippingLocationIds: vendor.locations.map((l) => l.id),
    },
    resolver: yupResolver(vendorSchema),
  })

  const brandIds = useWatch({
    name: 'brandIds',
    control: form.control,
    defaultValue: vendor.brands.map((b) => b.id),
  })

  const shippingLocationIds = useWatch({
    name: 'shippingLocationIds',
    control: form.control,
    defaultValue: vendor.locations.map((l) => l.id),
  })

  const { data: selectedBrandsData, previousData: selectedBrandsPreviousData } =
    useSearchBrandsQuery({
      client,
      variables: { ids: brandIds, searchTerm: null },
      skip: brandIds.length === 0,
    })

  const { data: selectedLocationsData, previousData: selectedLocationsPreviousData } =
    useShippingLocationsSearchQuery({
      client,
      variables: { orgId, page: 1, filters: [['in', 'ids', shippingLocationIds.join(',')]] },
      skip: shippingLocationIds.length === 0,
    })

  const vendorLabel = useMemo(() => (org ? createVendorLabel(org, isZachry) : ''), [org, isZachry])

  const selectedBrands =
    brandIds.length > 0
      ? (selectedBrandsData?.brands ?? selectedBrandsPreviousData?.brands ?? [])
      : []

  const selectedLocations =
    shippingLocationIds.length > 0
      ? (selectedLocationsData?.shippingLocationsSearch.shippingLocations ??
        selectedLocationsPreviousData?.shippingLocationsSearch.shippingLocations ??
        [])
      : []

  const onSubmit = async (values: VendorForm) => {
    try {
      await updateVendor({
        variables: {
          vendorId: vendor.id,
          name: values.name || '',
          accountNumbers: values.accountNumbers,
          brandIds: values.brandIds,
          offline: !!values.offline,
          fleetioId: values.fleetioId || null,
          externalVendorId: values.externalVendorId || null,
          shippingLocationIds: values.shippingLocationIds,
        },
      })

      onAfterSave()
    } catch (err) {
      console.error(err)
      // Say which rule rejected the save when the server named one.
      const message = (err as ApolloError).graphQLErrors?.[0]?.message

      messages.add(message || 'Oops! Looks like something went wrong.', 'negative')
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="p-4 flex flex-col gap-y-4 w-86">
      <Controller
        control={form.control}
        name="accountNumbers"
        render={({ field }) => (
          <Field label="Account Number">
            {field.value.map((value, i) => (
              <Fragment key={i}>
                <div className="flex gap-x-2 pb-1">
                  <TextInput
                    value={value}
                    ref={i === 0 ? field.ref : undefined}
                    onChange={(e) => {
                      const updated = [...field.value]
                      updated[i] = e.target.value
                      field.onChange(updated)
                    }}
                  />
                  {i > 0 && (
                    <button
                      type="button"
                      onClick={() => field.onChange(field.value.filter((_value, i2) => i2 !== i))}
                    >
                      <XMarkIcon className="text-gray-700 h-5 w-5" />
                    </button>
                  )}
                </div>
                <FieldError error={form.formState.errors.accountNumbers?.[i]?.message} />
              </Fragment>
            ))}

            <Action.T
              onClick={() => field.onChange([...field.value, ''])}
              className="text-sm no-underline flex items-center gap-x-1.5 pt-1"
            >
              <PlusIcon className="inline-block h-4 w-4" /> Add additional account number
            </Action.T>
          </Field>
        )}
      />

      <TextField
        label="Vendor Name"
        errors={form.formState.errors.name?.message}
        maxLength={99}
        {...form.register('name')}
      />

      <Controller
        control={form.control}
        name="brandIds"
        render={({ field }) => (
          <Field label="Brands">
            <AutocompleteMultiInputNext<
              SearchBrandsQuery,
              SearchBrandsQueryVariables,
              SearchBrandsQuery['brands'][number]
            >
              gqlClient={client}
              query={SearchBrandsDocument}
              queryVars={(search) => ({ searchTerm: search, ids: null })}
              onChange={(brands) => field.onChange(brands.map((b) => b.id))}
              transformQueryToData={(queryData) => queryData.brands}
              transformDatumToOption={(brand) => ({
                value: brand.id,
                label: brand.name,
              })}
              selectedValues={selectedBrands}
              // Filter out the locations the user is already subscribed to
              filterOption={(option) => !field.value.includes(option.value)}
              reactSelectOptions={{
                isClearable: false,
                menuPortalTarget: document.body,
              }}
            />
          </Field>
        )}
      />

      {showFleetioId && (
        <TextField
          label="Fleetio Vendor ID"
          errors={form.formState.errors.fleetioId?.message}
          type="number"
          {...form.register('fleetioId')}
        />
      )}

      {isZachry ? (
        <Controller
          control={form.control}
          name="externalVendorId"
          render={({ field, fieldState }) => (
            <Field label={vendorLabel}>
              <div className="pt-1">
                <ExternalVendorCodeSelector
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Search by vendor name or number"
                />
              </div>
              <FieldError error={fieldState.error?.message} />
            </Field>
          )}
        />
      ) : (
        <TextField
          label={`${vendorLabel} (optional)`}
          errors={form.formState.errors.externalVendorId?.message}
          type="text"
          maxLength={49}
          {...form.register('externalVendorId')}
        />
      )}

      <Controller
        control={form.control}
        name="shippingLocationIds"
        render={({ field }) => (
          <Field label="Locations">
            <AutocompleteMultiInputNext<
              ShippingLocationsSearchQuery,
              ShippingLocationsSearchQueryVariables,
              ShippingLocationsSearchQuery['shippingLocationsSearch']['shippingLocations'][number]
            >
              gqlClient={client}
              query={ShippingLocationsSearchDocument}
              queryVars={(search) => ({
                page: 1,
                orgId,
                filters: search ? [['contains', 'search', search]] : [],
              })}
              onChange={(locations) => field.onChange(locations.map((l) => l.id))}
              transformQueryToData={(queryData) =>
                queryData.shippingLocationsSearch.shippingLocations
              }
              transformDatumToOption={(location) => ({
                value: location.id,
                label: location.name,
              })}
              selectedValues={selectedLocations}
              // Filter out the locations the user is already subscribed to
              filterOption={(option) => !field.value.includes(option.value)}
              reactSelectOptions={{
                isClearable: false,
                menuPortalTarget: document.body,
              }}
            />
          </Field>
        )}
      />

      <div className="flex gap-x-4 mt-4">
        <Button
          type="button"
          className="font-semibold justify-center tracking-wide border-slate-500 text-slate-500 bg-transparent hover:bg-slate-50 w-1/3"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button type="submit" className="font-semibold justify-center tracking-wide w-2/3">
          Save
        </Button>
      </div>
    </form>
  )
}

export default VendorForm
